import { useState, useEffect } from 'react'
import { Mail, Eye, Pencil, CheckCircle2, XCircle, FileText, MoreHorizontal, ToggleLeft } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/use-toast'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { emailTemplateService } from '@/services/emailTemplateService'
import { EmailTemplate } from '@/services/emailService'
import { TemplateEditorDialog } from '@/components/email/TemplateEditorDialog'
import { TemplatePreviewDialog } from '@/components/email/TemplatePreviewDialog'
import { cn } from '@/lib/utils'

// İstatistik Bileşeni
function EmailTemplateStats({ templates }: { templates: EmailTemplate[] }) {
  const stats = {
    total: templates.length,
    active: templates.filter(t => t.isActive).length,
    inactive: templates.filter(t => !t.isActive).length,
  }

  const items = [
    {
      label: "Toplam Şablon",
      value: stats.total,
      icon: FileText,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "Aktif Şablon",
      value: stats.active,
      icon: CheckCircle2,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      label: "Pasif Şablon",
      value: stats.inactive,
      icon: XCircle,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
    },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {items.map((item) => (
        <Card key={item.label} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
          <CardContent className="p-0">
            <div className="relative">
              <div className={`absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full ${item.bgColor} blur-2xl`}></div>
              <div className="p-6 relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{item.label}</p>
                    <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{item.value}</h3>
                  </div>
                  <div className={cn("p-3.5 rounded-xl shadow-sm", item.bgColor)}>
                    <item.icon className={cn("w-5 h-5", item.color)} strokeWidth={2.5} />
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-slate-600">
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? item.bgColor.replace('/10', '') : "bg-slate-300"}`}></span>
                      <span className="font-medium">
                        {item.label === "Aktif Şablon" && `%${stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} aktif`}
                        {item.label === "Pasif Şablon" && `%${stats.total > 0 ? Math.round((item.value / stats.total) * 100) : 0} pasif`}
                        {item.label === "Toplam Şablon" && "Sistemde kayıtlı"}
                      </span>
                    </div>
                    <span className={cn("text-xs font-semibold", item.color)}>{item.value}/{stats.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export default function EmailTemplatesPage() {
  const { toast } = useToast()
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')

  // Şablonları getir
  const fetchTemplates = async () => {
    try {
      setIsLoading(true)
      const response = await emailTemplateService.getAll()
      setTemplates(response.data.data)
    } catch (error) {
      console.error('Şablonlar getirilirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Email şablonları getirilirken bir hata oluştu.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchTemplates()
  }, [])

  // Şablon aktif/pasif toggle
  const handleToggle = async (template: EmailTemplate) => {
    try {
      await emailTemplateService.toggle(template.slug)
      await fetchTemplates()
      toast({
        title: 'Başarılı',
        description: `Şablon ${template.isActive ? 'pasif' : 'aktif'} yapıldı.`
      })
    } catch (error) {
      console.error('Şablon durumu değiştirilirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Şablon durumu değiştirilirken bir hata oluştu.'
      })
    }
  }

  // Şablon önizleme
  const handlePreview = async (template: EmailTemplate) => {
    try {
      const response = await emailTemplateService.preview(template.slug)
      setPreviewHtml(response.data.data.html)
      setSelectedTemplate(template)
      setIsPreviewOpen(true)
    } catch (error) {
      console.error('Önizleme yüklenirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Şablon önizlemesi yüklenirken bir hata oluştu.'
      })
    }
  }

  // Şablon düzenleme
  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template)
    setIsEditorOpen(true)
  }

  // Şablon kaydetme
  const handleSave = async () => {
    await fetchTemplates()
    setIsEditorOpen(false)
    setSelectedTemplate(null)
  }

  if (isLoading) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-6 p-8 pt-6">
          {/* Hero Section Skeleton */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <div
              className="flex"
              style={{
                background: 'linear-gradient(to right, #7c3aed, #a855f7)',
              }}
            >
              <div className="p-8 flex justify-between items-start relative w-full">
                <div className="space-y-3 flex-1">
                  <Skeleton className="h-5 w-40 bg-white/20" />
                  <Skeleton className="h-9 w-64 bg-white/30" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[500px] bg-white/20" />
                    <Skeleton className="h-4 w-[450px] bg-white/20" />
                    <Skeleton className="h-4 w-[400px] bg-white/20" />
                  </div>
                </div>
                <Skeleton className="w-40 h-40 rounded-lg bg-white/10 ml-8" />
              </div>
            </div>
          </div>

          {/* Stats Cards Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>
                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[100px] mb-2" />
                          <Skeleton className="h-9 w-[50px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[80px]" />
                          </div>
                          <Skeleton className="h-4 w-[30px]" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Toolbar Skeleton */}
          <div className="flex items-center">
            <Skeleton className="h-5 w-[180px]" />
          </div>

          {/* Table Skeleton */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                  <TableHead className="py-4 pl-6 w-[280px]">
                    <Skeleton className="h-5 w-[120px]" />
                  </TableHead>
                  <TableHead className="py-4 w-[350px]">
                    <Skeleton className="h-5 w-[100px]" />
                  </TableHead>
                  <TableHead className="py-4 w-[120px]">
                    <Skeleton className="h-5 w-[60px]" />
                  </TableHead>
                  <TableHead className="py-4 w-[180px]">
                    <Skeleton className="h-5 w-[60px]" />
                  </TableHead>
                  <TableHead className="py-4 pr-6 w-[80px] text-right">
                    <Skeleton className="h-5 w-[60px] ml-auto" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i} className="border-b border-slate-100">
                    <TableCell className="py-4 pl-6">
                      <div className="space-y-1">
                        <Skeleton className="h-5 w-[140px]" />
                        <Skeleton className="h-4 w-[80px]" />
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="space-y-1">
                        <Skeleton className="h-5 w-[180px]" />
                        <Skeleton className="h-4 w-[120px]" />
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-7 w-[60px] rounded-lg" />
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-5 w-[80px]" />
                    </TableCell>
                    <TableCell className="py-4 pr-6 text-right">
                      <Skeleton className="h-9 w-9 rounded-lg ml-auto" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{
              background: 'linear-gradient(to right, #7c3aed, #a855f7)',
            }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <div className="text-white/80 text-sm font-medium">
                  Email Yönetim Paneli
                </div>
                <h1 className="text-3xl font-bold text-white">
                  Email Şablonları
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  Toplu mail şablonlarını yönetin. Şablonların içeriğini düzenleyebilir,
                  resim ekleyebilir ve buton metni/linki belirleyebilirsiniz.
                  <code className="bg-white/20 px-1 rounded mx-1">{'{{firma}}'}</code>
                  yazarak firma adını otomatik ekleyebilirsiniz.
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                <Mail className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* İstatistikler */}
        <EmailTemplateStats templates={templates} />

        {/* Toolbar */}
        <div className="flex items-center">
          <div className="text-sm text-muted-foreground">
            Toplam {templates.length} şablon bulunuyor
          </div>
        </div>

        {/* Şablon Tablosu */}
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
          <Table className="w-full table-fixed">
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
                <TableHead className="py-4 pl-6 text-slate-700 font-bold text-sm w-[280px]">Şablon Bilgileri</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[350px]">Konu / Başlık</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[120px]">Durum</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[180px]">Buton</TableHead>
                <TableHead className="text-right py-4 pr-6 text-slate-700 font-bold text-sm w-[80px]">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    <div className="flex flex-col items-center justify-center text-slate-500">
                      <FileText className="h-10 w-10 mb-2 text-slate-400" />
                      <p>Şablon bulunamadı.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow
                    key={template._id}
                    className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors"
                  >
                    {/* Şablon Bilgileri */}
                    <TableCell className="py-4 pl-6">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900 break-words text-base">
                          {template.name}
                        </span>
                        <span className="text-sm text-slate-500 mt-0.5">
                          {template.slug}
                        </span>
                      </div>
                    </TableCell>

                    {/* Konu / Başlık */}
                    <TableCell className="py-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900 break-words text-sm">
                          {template.subject}
                        </span>
                        <span className="text-xs text-slate-500 mt-0.5">
                          {template.title}
                        </span>
                      </div>
                    </TableCell>

                    {/* Durum */}
                    <TableCell className="py-4">
                      <div className="space-y-2">
                        {template.isActive ? (
                          <div className="inline-flex items-center rounded-lg bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                            <div className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-600"></div>
                            Aktif
                          </div>
                        ) : (
                          <div className="inline-flex items-center rounded-lg bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800">
                            <div className="mr-1.5 h-1.5 w-1.5 rounded-full bg-red-600"></div>
                            Pasif
                          </div>
                        )}
                      </div>
                    </TableCell>

                    {/* Buton */}
                    <TableCell className="py-4">
                      {template.buttonText ? (
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-slate-700">
                            {template.buttonText}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </TableCell>

                    {/* İşlemler */}
                    <TableCell className="text-right pr-6">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-lg hover:bg-slate-100">
                            <span className="sr-only">İşlemler</span>
                            <MoreHorizontal className="h-5 w-5 text-slate-600" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem onClick={() => handlePreview(template)}>
                            <Eye className="h-4 w-4 mr-2 text-slate-500" />
                            <span>Önizle</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(template)}>
                            <Pencil className="h-4 w-4 mr-2 text-slate-500" />
                            <span>Düzenle</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggle(template)}>
                            <ToggleLeft className="h-4 w-4 mr-2 text-slate-500" />
                            <span>{template.isActive ? 'Pasif Yap' : 'Aktif Yap'}</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Editor Dialog */}
        {selectedTemplate && (
          <TemplateEditorDialog
            isOpen={isEditorOpen}
            onClose={() => {
              setIsEditorOpen(false)
              setSelectedTemplate(null)
            }}
            template={selectedTemplate}
            onSave={handleSave}
          />
        )}

        {/* Preview Dialog */}
        <TemplatePreviewDialog
          isOpen={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false)
            setPreviewHtml('')
          }}
          html={previewHtml}
          templateName={selectedTemplate?.name || ''}
        />
      </div>
    </div>
  )
}
